import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';
import { css } from '@compiled/react';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';

import { ContentfulInstructorLedTraining } from 'src/graphql/queries/GetInstructorLedTraining';
import { useRichTextEmbeddedAssets } from 'src/hooks/useRichTextEmbeddedAssets';

interface ContentProps {
  contentfulILTData?: ContentfulInstructorLedTraining;
}

export const RichContent = ({ contentfulILTData }: ContentProps) => {
  const hasDescription = contentfulILTData?.description?.json;
  const renderOptions = useRichTextEmbeddedAssets(contentfulILTData?.description?.links);

  if (!hasDescription) {
    return null;
  }

  return (
    <div css={descriptionSectionStyles}>
      <div css={descriptionContentStyles}>
        <div css={richTextContentStyles}>
          {documentToReactComponents(contentfulILTData.description.json, renderOptions)}
        </div>
      </div>
    </div>
  );
};

// Base styles shared by all headings
const baseHeadingStyles = {
  fontFeatureSettings: "'liga' off, 'clig' off",
  fontFamily: 'Charlie Display, sans-serif',
  margin: 0,
  marginBottom: '1.5rem',
  '&:last-child': {
    marginBottom: 0,
  },
};

// Helper to create heading styles with specific sizes and weight
const createHeadingStyles = (
  fontWeight: number,
  desktopSize: string,
  desktopLineHeight: string,
  mobileSize: string,
  mobileLineHeight: string,
) => ({
  ...baseHeadingStyles,
  fontWeight,
  fontSize: desktopSize,
  lineHeight: desktopLineHeight,
  [media.below.md]: {
    fontSize: mobileSize,
    lineHeight: mobileLineHeight,
  },
});

// Shared style for bold/strong within headings
const headingInheritStyles = {
  fontFamily: 'inherit',
  fontSize: 'inherit',
  fontWeight: 'inherit',
};

const descriptionSectionStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.400'),
});

const descriptionContentStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: token('space.200'),
});

const richTextContentStyles = css({
  color: token('color.text'),
  fontFamily: 'Inter, sans-serif',
  fontSize: '1.25rem',
  fontStyle: 'normal',
  fontWeight: 400,
  lineHeight: '2rem',

  [media.below.md]: {
    fontSize: '1rem',
    lineHeight: '1.5rem',
  },

  '& p': {
    margin: 0,
    marginBottom: '1.5rem',

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '& b, & strong': {
    fontWeight: 700,
  },

  '& h1': createHeadingStyles(700, '3rem', '3.75rem', '2.5rem', '3.125rem'),
  '& h1 b, & h1 strong': headingInheritStyles,

  '& h2': createHeadingStyles(700, '2.5rem', '3.125rem', '2rem', '2.5rem'),
  '& h2 b, & h2 strong': headingInheritStyles,

  '& h3': createHeadingStyles(700, '2rem', '2.5rem', '1.5rem', '1.875rem'),
  '& h3 b, & h3 strong': headingInheritStyles,

  '& h4': createHeadingStyles(600, '1.5rem', '1.875rem', '1.25rem', '1.5625rem'),
  '& h4 b, & h4 strong': headingInheritStyles,

  '& h5': createHeadingStyles(600, '1.25rem', '1.5625rem', '1rem', '1.25rem'),
  '& h5 b, & h5 strong': headingInheritStyles,

  '& h6': createHeadingStyles(600, '1rem', '1.25rem', '0.875rem', '1.125rem'),
  '& h6 b, & h6 strong': headingInheritStyles,

  '& a': {
    color: token('color.link'),
    textDecoration: 'underline',

    '&:hover': {
      color: token('color.link.pressed'),
    },
  },

  '& ul, & ol': {
    margin: 0,
    marginBottom: '1.5rem',
    paddingLeft: token('space.400'),

    '&:last-child': {
      marginBottom: 0,
    },
  },

  '& li': {
    marginBottom: token('space.100'),

    '&:last-child': {
      marginBottom: 0,
    },
  },
});
